import { httpsCallable } from "firebase/functions";
import { useMemo, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { functions } from "../firebase";
import { PLAN_DEFINITIONS } from "../plans";

const tiers = [
  {
    ...PLAN_DEFINITIONS.free,
    badge: "Starter",
    cta: "Get Started",
    features: PLAN_DEFINITIONS.free.analyticsFeatures
  },
  {
    ...PLAN_DEFINITIONS.pro,
    badge: "7-Day Free Trial",
    cta: "Start Free Trial",
    featured: true,
    features: [
      ...PLAN_DEFINITIONS.pro.analyticsFeatures,
      "Priority support",
      "Charged after 7 days unless canceled"
    ]
  },
  {
    ...PLAN_DEFINITIONS.team,
    badge: "Creator Team",
    cta: "Unlock Team",
    features: [
      ...PLAN_DEFINITIONS.team.analyticsFeatures,
      "Invites and workspace collaboration"
    ]
  }
];

export default function Pricing() {
  const { user, userProfile } = useAuth();
  const [loadingPlanId, setLoadingPlanId] = useState("");
  const [error, setError] = useState("");
  const location = useLocation();
  const navigate = useNavigate();

  const billingMessage = useMemo(() => {
    const params = new URLSearchParams(location.search);
    const status = params.get("billing");

    if (status === "success") {
      return "Billing completed. Your plan will update as soon as Stripe confirms the subscription.";
    }

    if (status === "cancelled") {
      return "Checkout was cancelled. Your current plan was not changed.";
    }

    return "";
  }, [location.search]);

  const startCheckout = async (planId) => {
    if (!user) {
      navigate("/login", { state: { from: "/pricing" } });
      return;
    }

    setLoadingPlanId(planId);
    setError("");

    try {
      const createCheckoutSession = httpsCallable(functions, "createCheckoutSession");
      const result = await createCheckoutSession({
        planId,
        origin: window.location.origin
      });

      window.location.assign(result.data.url);
    } catch (checkoutError) {
      setError(checkoutError.message || "Unable to start checkout.");
      setLoadingPlanId("");
    }
  };

  return (
    <div className="container pricing-shell pricing-page">
      <section className="hero-card pricing-hero">
        <span className="eyebrow">Pricing</span>
        <h1>Simple pricing for creator affiliate analytics.</h1>
        <p className="hero-lead">
          Start on Basic, upgrade to Pro for more content and platform tracking, or move into Team for a mentor-led
          shared creator workspace with 5 seats.
        </p>
        <p className="muted-text">
          Pro now includes a 7-day free trial so new users can explore the full solo plan before the first charge.
        </p>
        {billingMessage ? <p className="success-text">{billingMessage}</p> : null}
        {error ? <p className="error-text">{error}</p> : null}
      </section>

      <section className="pricing-grid">
        {tiers.map((tier) => {
          const isCurrentPlan = userProfile?.planId === tier.id;
          const isFreeTier = tier.monthlyPrice === 0;
          const isLoading = loadingPlanId === tier.id;

          return (
            <article className={tier.featured ? "card pricing-card pricing-card-featured" : "card pricing-card"} key={tier.id}>
              <div className="pricing-badge-row">
                <span className={tier.featured ? "pricing-badge pricing-badge-featured" : "pricing-badge"}>
                  {tier.badge}
                </span>
              </div>

              <div className="pricing-header">
                <h2>{tier.name}</h2>
                <div className="pricing-amount">
                  <strong>{tier.priceLabel}</strong>
                  <span>{tier.monthlyPrice ? "/mo" : "free"}</span>
                </div>
              </div>

              <p className="pricing-description">{tier.description}</p>
              {tier.trialDays ? <p className="pricing-trial-note">{tier.trialDays}-day free trial, then {tier.priceLabel}/mo if not canceled.</p> : null}
              <p className="pricing-cap">{tier.programLimit} content links</p>

              <ul className="feature-list pricing-feature-list">
                <li>{tier.userLimit} user{tier.userLimit === 1 ? "" : "s"}</li>
                {tier.features.map((feature) => (
                  <li key={feature}>{feature}</li>
                ))}
              </ul>

              {isCurrentPlan ? (
                <button disabled type="button">
                  Current plan
                </button>
              ) : isFreeTier ? (
                <Link className={tier.featured ? "cta-button" : "ghost-link"} to={user ? "/dashboard" : "/login"}>
                  {user ? "Use Basic" : tier.cta}
                </Link>
              ) : (
                <button disabled={isLoading} onClick={() => startCheckout(tier.id)} type="button">
                  {isLoading ? "Redirecting..." : tier.cta}
                </button>
              )}
            </article>
          );
        })}
      </section>

      <section className="card pricing-notes">
        <div>
          <h2>Billing and cancellation</h2>
          <p>
            Paid subscriptions renew monthly until canceled. You can cancel before your next billing cycle to prevent
            future renewal charges.
          </p>
          <p>
            Pro trial users are charged after the 7-day trial ends unless the subscription is canceled before renewal.
          </p>
        </div>

        <div>
          <h2>Refund guidance</h2>
          <p>
            Refund requests are reviewed case by case for duplicate charges, accidental renewals reported promptly, or
            technical billing errors.
          </p>
        </div>

        <div>
          <h2>Support contact</h2>
          <p>
            For billing or account questions, contact <strong>support@affitrack.net</strong>.
          </p>
        </div>
      </section>
    </div>
  );
}
